import { useState } from "react";
import { useParkSchedule } from "../../hooks/useParkSchedule";
import type { WikiScheduleDay } from "../../config/themeparksWiki";
import { zoneNote } from "../../utils/tripDisplay";
import "./themeparksWiki.css";

interface ParkScheduleCardProps {
  parkExternalId: string;
}

/** A park time on the park's own clock — 8:00 AM in Anaheim reads 8:00 AM
 *  from Boston too, not 11:00. */
function fmtTime(iso: string | null | undefined, zone?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: zone });
  } catch {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
}

function prettyType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace(/_/g, " ");
}

function fmtHours(day: WikiScheduleDay | null, zone?: string): string {
  if (!day) return "Hours unavailable";
  if (day.type !== "OPERATING") {
    return day.description || prettyType(day.type);
  }
  const open = fmtTime(day.openingTime, zone);
  const close = fmtTime(day.closingTime, zone);
  const range = open && close ? `${open} – ${close}` : open || close;
  if (!range) return "Open";
  // Named when the reader's phone is on another clock, so "closes 7:00 PM"
  // read from Boston isn't taken for Boston's 7 PM. At the park, no label.
  const at = day.openingTime ?? day.closingTime;
  const note = at ? zoneNote(new Date(at), zone) : null;
  return note ? `${range} ${note}` : range;
}

function fmtDayLabel(dateKey: string): string {
  const parts = dateKey.split("-").map((n) => parseInt(n, 10));
  const [y, m, d] = parts;
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function fmtRelative(ms: number): string {
  const hrs = Math.floor((Date.now() - ms) / 3_600_000);
  if (hrs < 1) return "updated just now";
  if (hrs < 24) return `updated ${hrs}h ago`;
  return `updated ${Math.floor(hrs / 24)}d ago`;
}

/**
 * Today's park hours, shown inline under the park title. If there are upcoming
 * days cached, the row becomes a toggle that expands the full schedule.
 */
export function ParkScheduleCard({ parkExternalId }: ParkScheduleCardProps) {
  const [open, setOpen] = useState(false);
  const { today, upcoming, fetchedAt, stale, loading, timezone } = useParkSchedule(parkExternalId);

  if (loading) return null;
  if (!today && upcoming.length === 0 && fetchedAt == null) return null;

  const todayOpen = !!today && today.type === "OPERATING";
  const hasMore = upcoming.length > 0;

  return (
    <div className="park-hours">
      <button
        type="button"
        className="park-hours-summary"
        onClick={() => { if (hasMore) setOpen((o) => !o); }}
        disabled={!hasMore}
        aria-expanded={hasMore ? open : undefined}
      >
        <span className="park-hours-label">Today</span>
        <span className={todayOpen ? "park-hours-value" : "park-hours-value park-hours-value-closed"}>
          {fmtHours(today, timezone)}
        </span>
        {hasMore && (
          <span
            className={open ? "park-hours-caret park-hours-caret-open" : "park-hours-caret"}
            aria-hidden="true"
          >
            ▾
          </span>
        )}
      </button>

      {open && hasMore && (
        <ul className="park-hours-list">
          {upcoming.map((d) => (
            <li key={d.date}>
              <span>{fmtDayLabel(d.date)}</span>
              <span className={d.type === "OPERATING" ? "" : "park-hours-value-closed"}>{fmtHours(d, timezone)}</span>
            </li>
          ))}
          {fetchedAt != null && (
            <li className="park-hours-meta">
              <span>{stale ? "May be out of date" : "Park hours"}</span>
              <span>{fmtRelative(fetchedAt)}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
